/**
 * Displacement map for the Liquid Glass lens: a rounded rect whose rim is a convex squircle bezel.
 * feDisplacementMap samples P(x + scale*(R-0.5), y + scale*(G-0.5)); the backdrop only exists inside
 * the element, so the rim samples inward and the flat centre stays neutral (128, 128).
 */
const REFRACTIVE_INDEX = 1.5

function deviation(x: number): number {
  const u = 1 - Math.min(Math.max(x, 1e-4), 1)
  const slope = u ** 3 * (1 - u ** 4) ** -0.75
  const incident = Math.atan(slope)
  const refracted = Math.asin(Math.sin(incident) / REFRACTIVE_INDEX)
  return Math.tan(incident - refracted)
}

const MAX_DEVIATION = deviation(1e-4)

/** RGBA pixels, row-major, for an element of w x h px. */
export function glassMapPixels(w: number, h: number, radius: number, bezel: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(w * h * 4)
  const r = Math.min(radius, w / 2, h / 2)
  const halfX = w / 2 - r
  const halfY = h / 2 - r
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const px = i + 0.5 - w / 2
      const py = j + 0.5 - h / 2
      const qx = Math.abs(px) - halfX
      const qy = Math.abs(py) - halfY
      let depth: number
      let nx = 0
      let ny = 0
      if (qx > 0 && qy > 0) {
        const len = Math.hypot(qx, qy)
        depth = r - len
        nx = qx / len
        ny = qy / len
      } else if (qx > qy) {
        depth = r - qx
        nx = 1
      } else {
        depth = r - qy
        ny = 1
      }
      let red = 128
      let green = 128
      if (depth > 0 && depth < bezel) {
        const m = deviation(depth / bezel) / MAX_DEVIATION
        red = Math.round(127.5 - 127.5 * Math.sign(px) * nx * m)
        green = Math.round(127.5 - 127.5 * Math.sign(py) * ny * m)
      }
      const o = (j * w + i) * 4
      out[o] = red
      out[o + 1] = green
      out[o + 2] = 128
      out[o + 3] = 255
    }
  }
  return out
}

const cache = new Map<string, string>()

/** PNG data URL of the map, cached per size. Browser only. */
export function glassMapUrl(w: number, h: number, radius: number, bezel: number): string {
  const key = `${w}x${h}r${radius}b${bezel}`
  const hit = cache.get(key)
  if (hit) return hit
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.putImageData(new ImageData(glassMapPixels(w, h, radius, bezel), w, h), 0, 0)
  const url = canvas.toDataURL('image/png')
  // The top bar is fluid, so a window drag produces many sizes; keep only recent ones.
  if (cache.size >= 32) cache.delete(cache.keys().next().value as string)
  cache.set(key, url)
  return url
}

/** The lens needs SVG filters in backdrop-filter, which only Chromium renders. Safari accepts the syntax and draws nothing. */
export function supportsGlassLens(ua = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /Chrome\//.test(ua) && !/Firefox\//.test(ua)
}
